import React from 'react';
import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import {
  buildCampSeries,
  formatCampWeekName,
  formatDateForRegion,
  type LessonFormData,
  type Region,
} from '@court-sync/shared';
import { Input, Field, FieldLabel, FieldError } from '@/components/ui';
import { CAMP_DAY_LABELS } from '@/utils/campLessonForm';

type CampLessonScheduleFieldsProps = {
  register: UseFormRegister<LessonFormData>;
  errors: FieldErrors<LessonFormData>;
  selectedDaysOfWeek: number[];
  onToggleDay: (dayIndex: number) => void;
  computedWeekDates: string[];
  region?: Region | null;
  disabled?: boolean;
  showRepeatWeekly?: boolean;
  weekCount?: number;
  onWeekCountChange?: (count: number) => void;
  campNameForPreview?: string;
};

export function CampLessonScheduleFields({
  register,
  errors,
  selectedDaysOfWeek,
  onToggleDay,
  computedWeekDates,
  region,
  disabled = false,
  showRepeatWeekly = false,
  weekCount = 1,
  onWeekCountChange,
  campNameForPreview = 'Camp',
}: CampLessonScheduleFieldsProps) {
  const campSeriesPreview =
    showRepeatWeekly && weekCount > 1 && computedWeekDates.length > 0
      ? buildCampSeries(computedWeekDates, weekCount)
      : [];

  return (
    <>
      <Field data-invalid={!!errors.startDate?.message}>
        <FieldLabel htmlFor="campLessonStartDate">Start date *</FieldLabel>
        <Input
          id="campLessonStartDate"
          type="date"
          aria-invalid={!!errors.startDate?.message}
          disabled={disabled}
          {...register('startDate')}
        />
        <FieldError>{errors.startDate?.message}</FieldError>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          First day of the camp week. End date is computed from the last selected weekday in that 7-day
          window.
        </p>
      </Field>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Days of week * ({selectedDaysOfWeek.length} selected)
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2">
          {CAMP_DAY_LABELS.map((label, index) => (
            <label
              key={label}
              className={`flex items-center justify-center p-2 border rounded-md transition-colors text-sm font-medium ${
                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
              } ${
                selectedDaysOfWeek.includes(index)
                  ? 'bg-blue-50 border-blue-500 text-blue-800 dark:bg-blue-950 dark:border-blue-400 dark:text-blue-200'
                  : 'bg-white border-gray-300 text-gray-700 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                disabled={disabled}
                checked={selectedDaysOfWeek.includes(index)}
                onChange={() => onToggleDay(index)}
              />
              {label}
            </label>
          ))}
        </div>
        {selectedDaysOfWeek.length === 0 ? (
          <p className="text-sm text-red-600 dark:text-red-400 mt-1">Select at least one day of the week.</p>
        ) : null}
        {errors.daysOfWeek ? (
          <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.daysOfWeek.message}</p>
        ) : null}
        {computedWeekDates.length > 0 ? (
          <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
            Camp week:{' '}
            {formatDateForRegion(computedWeekDates[0]!, region, {
              month: 'short',
              day: 'numeric',
            })}
            {computedWeekDates.length > 1
              ? ` – ${formatDateForRegion(
                  computedWeekDates[computedWeekDates.length - 1]!,
                  region,
                  { month: 'short', day: 'numeric', year: 'numeric' }
                )}`
              : ''}{' '}
            ({computedWeekDates.length} day{computedWeekDates.length !== 1 ? 's' : ''})
          </p>
        ) : null}
      </div>

      {showRepeatWeekly ? (
        <div className="md:col-span-2 space-y-3">
          <Field>
            <FieldLabel htmlFor="campLessonWeekCount">Repeat weekly</FieldLabel>
            <Input
              id="campLessonWeekCount"
              type="number"
              min={1}
              max={52}
              disabled={disabled}
              value={weekCount}
              onWheel={(e) => e.currentTarget.blur()}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                onWeekCountChange?.(Number.isNaN(n) ? 1 : Math.min(52, Math.max(1, n)));
              }}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Create multiple camp weeks with the same schedule. Week 1 uses the start date and weekdays above;
              later weeks shift forward by 7 days each.
            </p>
          </Field>
          {campSeriesPreview.length > 0 ? (
            <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4 dark:border-blue-800 dark:bg-blue-950/30">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
                Preview ({campSeriesPreview.length} weeks)
              </p>
              <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
                {campSeriesPreview.map((week) => {
                  const sorted = [...week.dates].sort();
                  const rangeStart = sorted[0];
                  const rangeEnd = sorted[sorted.length - 1];
                  return (
                    <li key={week.weekIndex}>
                      <span className="font-medium">
                        {formatCampWeekName(campNameForPreview, week.weekIndex, weekCount)}
                      </span>
                      {rangeStart && rangeEnd ? (
                        <span className="text-blue-700/80 dark:text-blue-400">
                          {' '}
                          ·{' '}
                          {formatDateForRegion(rangeStart, region, {
                            month: 'short',
                            day: 'numeric',
                          })}
                          {rangeStart !== rangeEnd
                            ? ` – ${formatDateForRegion(rangeEnd, region, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}`
                            : ''}{' '}
                          ({week.dates.length} day{week.dates.length !== 1 ? 's' : ''})
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

type CampLessonPricingFieldsProps = {
  register: UseFormRegister<LessonFormData>;
  errors: FieldErrors<LessonFormData>;
  currencySymbol: string;
  disabled?: boolean;
};

export function CampLessonPricingFields({
  register,
  errors,
  currencySymbol,
  disabled = false,
}: CampLessonPricingFieldsProps) {
  return (
    <>
      <Field data-invalid={!!errors.campDayPrice?.message}>
        <FieldLabel htmlFor="campDayPrice">Price per day ({currencySymbol}) *</FieldLabel>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
            {currencySymbol}
          </span>
          <Input
            id="campDayPrice"
            type="number"
            step="0.01"
            disabled={disabled}
            aria-invalid={!!errors.campDayPrice?.message}
            onWheel={(e) => e.currentTarget.blur()}
            {...register('campDayPrice', { valueAsNumber: true })}
            className="pl-12"
            placeholder="45.00"
          />
        </div>
        <FieldError>{errors.campDayPrice?.message}</FieldError>
      </Field>

      <Field data-invalid={!!errors.campFullPrice?.message}>
        <FieldLabel htmlFor="campFullPrice">Full camp package ({currencySymbol}) *</FieldLabel>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
            {currencySymbol}
          </span>
          <Input
            id="campFullPrice"
            type="number"
            step="0.01"
            disabled={disabled}
            aria-invalid={!!errors.campFullPrice?.message}
            onWheel={(e) => e.currentTarget.blur()}
            {...register('campFullPrice', { valueAsNumber: true })}
            className="pl-12"
            placeholder="200.00"
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Stored as the lesson&apos;s primary price for payments and reporting.
        </p>
        <FieldError>{errors.campFullPrice?.message}</FieldError>
      </Field>
    </>
  );
}
